/**
 * Hook for accessing client isolation context throughout the SCULPTD APP
 * Provides client context without changing UI aesthetics
 */

import { useEffect, useState } from 'react';
import { clientIsolationService, ClientContext } from '../services/ClientIsolationService';

export interface UseClientIsolationReturn {
  clientContext: ClientContext | null;
  isLoading: boolean;
  error: string | null;
  getClientHeaders: () => Record<string, string>;
  createIsolatedRequest: (url: string, options?: RequestInit) => RequestInit;
  validateAccess: (operation: string) => boolean;
  encryptData: (data: any) => string;
  decryptData: (encryptedData: string) => any;
}

export const useClientIsolation = (): UseClientIsolationReturn => {
  const [clientContext, setClientContext] = useState<ClientContext | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeClient = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Get existing client context or wait for initialization
        const context = clientIsolationService.getClientContext();
        
        if (context) {
          setClientContext(context);
        } else {
          // Wait a bit for initialization to complete
          setTimeout(() => {
            const context = clientIsolationService.getClientContext();
            if (context) {
              setClientContext(context);
            } else {
              setError('Client context not available');
            }
            setIsLoading(false);
          }, 100);
        }
        
        setIsLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to initialize client context');
        setIsLoading(false);
      }
    };

    initializeClient();
  }, []);

  const getClientHeaders = (): Record<string, string> => {
    if (!clientContext) {
      throw new Error('Client context not initialized');
    }
    return clientIsolationService.getClientHeaders();
  };

  const createIsolatedRequest = (url: string, options: RequestInit = {}): RequestInit => {
    if (!clientContext) {
      throw new Error('Client context not initialized');
    }
    return clientIsolationService.createIsolatedRequest(url, options);
  };

  const validateAccess = (operation: string): boolean => {
    if (!clientContext) {
      return false;
    }
    return clientIsolationService.validateAccess(operation);
  };

  const encryptData = (data: any): string => {
    if (!clientContext) {
      throw new Error('Client context not initialized');
    }
    return clientIsolationService.encryptData(data);
  };

  const decryptData = (encryptedData: string): any => {
    if (!clientContext) {
      throw new Error('Client context not initialized');
    }
    return clientIsolationService.decryptData(encryptedData);
  };

  return {
    clientContext,
    isLoading,
    error,
    getClientHeaders,
    createIsolatedRequest,
    validateAccess,
    encryptData,
    decryptData
  };
};